import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Image, 
  MapPin, 
  Layers, 
  LogOut,
  Plus,
  Trash2,
  Save,
  Menu,
  Star,
  GripVertical,
  Wrench,
  Briefcase,
  Upload,
  Video,
  Users,
  Scale,
  Check,
  Loader2,
  MessageCircle,
  Mail,
  DollarSign,
  Euro,
  ImagePlus,
  BarChart3,
  Globe,
  ChevronUp,
  ChevronDown,
  Shield,
  TrendingUp,
  MapPinned,
  X,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import PortalsAdmin from '../components/admin/PortalsAdmin';
import FaqAdmin from '../components/admin/FaqAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get video thumbnail URL
// Erkennt auch YouTube Shorts (/shorts/ID) – daran scheiterte die alte Website
function getVideoThumbnail(item) {
  if (item.poster_url || item.thumbnail_url) {
    const posterUrl = item.poster_url || item.thumbnail_url;
    return posterUrl.startsWith('/api') ? `${API_URL}${posterUrl}` : posterUrl;
  }

  const url = item.url || '';
  const ytPatterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of ytPatterns) {
    const m = url.match(re);
    if (m) {
      // Shorts im Originalformat (hochkant) statt 16:9 mit Balken
      return /\/shorts\//.test(url)
        ? `https://i.ytimg.com/vi/${m[1]}/oar2.jpg`
        : `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
    }
  }

  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://vumbnail.com/${vm[1]}.jpg`;

  return 'https://placehold.co/400x400/202020/6F6A63?text=Video';
}

// Service Item Form component with its own state
function ServiceItemForm({ categoryId, onAdd }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (!title || !description) {
      toast.error('Bitte füllen Sie Titel und Beschreibung aus');
      return;
    }
    onAdd(title, description);
    setTitle('');
    setDescription('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <Label className="text-foreground text-sm">Service-Titel</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z.B. Imagefilm, Produktfotografie..."
          className="bg-background border-border text-foreground"
        />
      </div>
      <div>
        <Label className="text-foreground text-sm">Beschreibung</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Kurze Beschreibung des Services"
          className="bg-background border-border text-foreground"
        />
      </div>
      <div className="flex items-end">
        <Button onClick={handleSubmit} className="w-full">
          <Plus className="w-4 h-4 mr-2" /> Service hinzufügen
        </Button>
      </div>
    </div>
  );
}

// Autosave status component
function AutosaveStatus({ status }) {
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        Speichern...
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-green-500">
        <Check className="w-3 h-3" />
        Gespeichert
      </span>
    );
  }
  return null;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const serviceImageInputRef = useRef(null);
  
  // Autosave state
  const [autosaveStatus, setAutosaveStatus] = useState(null);
  const autosaveTimerRef = useRef(null);
  
  // Data states
  const [content, setContent] = useState([]);
  const [media, setMedia] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [legalPages, setLegalPages] = useState({
    privacy: { title: 'Privacy Policy', body: '' },
    terms: { title: 'Terms of Service', body: '' },
    imprint: { title: 'Imprint', body: '' },
  });
  const [settings, setSettings] = useState({ 
    maintenance_mode: false, 
    show_hero_text: false, 
    contact_email: 'office@whitelabel-collective.com',
    whatsapp_link: '',
    budget_currency: 'EUR'
  });
  const [loading, setLoading] = useState(true);

  // Form states
  const [editingContent, setEditingContent] = useState({});
  const [newMedia, setNewMedia] = useState({ title: '', type: 'image', url: '', categories: '', featured: false });
  const [newLocation, setNewLocation] = useState({ name: '', coordinates: [0, 0], description: '', active: true });
  const [newCategory, setNewCategory] = useState({ name: '', slug: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeLegalPage, setActiveLegalPage] = useState('privacy');
  
  // Portfolios (Portale) & FAQ
  const [portals, setPortals] = useState([]);
  const [faqItems, setFaqItems] = useState([]);

  // Service categories state
  const [serviceCategories, setServiceCategories] = useState([]);
  const [newServiceCategory, setNewServiceCategory] = useState({ title: '', description: '', items: [] });
  const [editingServiceCategory, setEditingServiceCategory] = useState(null);
  const [newServiceItem, setNewServiceItem] = useState({ title: '', description: '' });
  
  // Stats state
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // SEO image upload state
  const seoImageInputRef = useRef(null);
  const [uploadingSeoImage, setUploadingSeoImage] = useState(false);
  
  // Master password state
  const [masterPasswordInput, setMasterPasswordInput] = useState('');

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/admin/login');
    } else if (token) {
      fetchAllData();
    }
  }, [authLoading, token, navigate]);

  // Autosave function with debounce
  const triggerAutosave = useCallback((saveFunction) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    
    setAutosaveStatus('saving');
    
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        await saveFunction();
        setAutosaveStatus('saved');
        setTimeout(() => setAutosaveStatus(null), 2000);
      } catch (error) {
        setAutosaveStatus(null);
        toast.error('Autosave fehlgeschlagen');
      }
    }, 1000);
  }, []);

  const fetchAllData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [contentRes, mediaRes, locationsRes, categoriesRes, settingsRes, logosRes, serviceCategoriesRes, contactRes, portalsRes, faqRes] = await Promise.all([
        fetch(`${API_URL}/api/content`, { headers }),
        fetch(`${API_URL}/api/media`, { headers }),
        fetch(`${API_URL}/api/locations?active_only=false`, { headers }),
        fetch(`${API_URL}/api/categories`, { headers }),
        fetch(`${API_URL}/api/settings`, { headers }),
        fetch(`${API_URL}/api/client-logos`, { headers }),
        fetch(`${API_URL}/api/service-categories`, { headers }),
        fetch(`${API_URL}/api/contact-submissions`, { headers }),
        fetch(`${API_URL}/api/portals?active_only=false`, { headers }),
        fetch(`${API_URL}/api/faq`, { headers }),
      ]);

      const [contentData, mediaData, locationsData, categoriesData] = await Promise.all([
        contentRes.json(),
        mediaRes.json(),
        locationsRes.json(),
        categoriesRes.json(),
      ]);

      let settingsData = { maintenance_mode: false, show_hero_text: false, contact_email: 'office@whitelabel-collective.com', whatsapp_link: '', budget_currency: 'EUR' };
      if (settingsRes.ok) {
        settingsData = await settingsRes.json();
      }

      let logosData = [];
      if (logosRes.ok) {
        logosData = await logosRes.json();
      }

      let serviceCategoriesData = [];
      if (serviceCategoriesRes.ok) {
        serviceCategoriesData = await serviceCategoriesRes.json();
      }

      let contactData = [];
      if (contactRes.ok) {
        contactData = await contactRes.json();
      }

      if (portalsRes.ok) {
        setPortals(await portalsRes.json());
      }
      if (faqRes.ok) {
        setFaqItems(await faqRes.json());
      }

      // Fetch legal pages
      const legalPagesData = {};
      for (const pageType of ['privacy', 'terms', 'imprint']) {
        try {
          const res = await fetch(`${API_URL}/api/legal/${pageType}`, { headers });
          if (res.ok) {
            legalPagesData[pageType] = await res.json();
          }
        } catch (e) {
          console.error(`Error fetching ${pageType}:`, e);
        }
      }

      setContent(contentData);
      setMedia(mediaData);
      setLocations(locationsData);
      setCategories(categoriesData);
      setSettings(settingsData);
      setClientLogos(logosData);
      setServiceCategories(serviceCategoriesData);
      setContactSubmissions(contactData);
      setLegalPages({
        privacy: legalPagesData.privacy || { title: 'Privacy Policy', body: '' },
        terms: legalPagesData.terms || { title: 'Terms of Service', body: '' },
        imprint: legalPagesData.imprint || { title: 'Imprint', body: '' },
      });

      // Initialize editing content
      const contentObj = {};
      contentData.forEach(item => {
        contentObj[`${item.section}_${item.key}`] = item.value;
      });
      setEditingContent(contentObj);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  // Fetch visitor statistics
  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API_URL}/api/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // SEO Image upload handler
  const handleUploadSeoImage = async (file) => {
    if (!file) return;
    
    setUploadingSeoImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/settings/seo-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({ ...settings, seo_og_image: data.url });
        toast.success('SEO-Bild hochgeladen');
      } else {
        toast.error('Bild konnte nicht hochgeladen werden');
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingSeoImage(false);
      if (seoImageInputRef.current) seoImageInputRef.current.value = '';
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ [key]: value }),
      });
      setSettings({ ...settings, [key]: value });
      toast.success('Einstellung aktualisiert');
    } catch (error) {
      toast.error('Einstellung konnte nicht aktualisiert werden');
    }
  };

  const handleContentUpdate = async (key, value, section) => {
    try {
      await fetch(`${API_URL}/api/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ key, value, section }),
      });
      toast.success('Inhalt aktualisiert');
      fetchAllData();
    } catch (error) {
      toast.error('Inhalt konnte nicht aktualisiert werden');
    }
  };

  // Autosave content handler
  const handleContentAutosave = (key, value, section) => {
    setEditingContent(prev => ({ ...prev, [`${section}_${key}`]: value }));
    triggerAutosave(() => handleContentUpdate(key, value, section));
  };

  const handleUploadImage = async () => {
    if (!uploadFile || !newMedia.title) {
      toast.error('Bitte wählen Sie eine Datei und geben Sie einen Titel ein');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', newMedia.title);
      formData.append('categories', newMedia.categories);
      formData.append('featured', newMedia.featured);

      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        toast.success('Bild hochgeladen');
        setNewMedia({ title: '', type: 'image', url: '', categories: '', featured: false });
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchAllData();
      } else {
        toast.error('Bild konnte nicht hochgeladen werden');
      }
    } catch (error) {
      toast.error('Bild konnte nicht hochgeladen werden');
    } finally {
      setUploading(false);
    }
  };

  const handleAddVideoMedia = async () => {
    if (!newMedia.title || !newMedia.url) {
      toast.error('Bitte geben Sie Titel und Video-URL ein');
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newMedia,
          type: 'video',
          categories: newMedia.categories.split(',').map(c => c.trim()).filter(c => c),
        }),
      });
      toast.success('Video hinzugefügt');
      setNewMedia({ title: '', type: 'image', url: '', categories: '', featured: false });
      fetchAllData();
    } catch (error) {
      toast.error('Video konnte nicht hinzugefügt werden');
    }
  };

  const handleDeleteMedia = async (id) => {
    try {
      await fetch(`${API_URL}/api/media/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Medien gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error('Medien konnten nicht gelöscht werden');
    }
  };

  const handleToggleFeatured = async (item) => {
    try {
      await fetch(`${API_URL}/api/media/${item.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ featured: !item.featured }),
      });
      toast.success('Medien aktualisiert');
      fetchAllData();
    } catch (error) {
      toast.error('Medien konnten nicht aktualisiert werden');
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.name) {
      toast.error('Bitte geben Sie einen Standortnamen ein');
      return;
    }
    try {
      await fetch(`${API_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newLocation),
      });
      toast.success('Standort hinzugefügt');
      setNewLocation({ name: '', coordinates: [0, 0], description: '', active: true });
      fetchAllData();
    } catch (error) {
      toast.error('Standort konnte nicht hinzugefügt werden');
    }
  };

  const handleDeleteLocation = async (id) => {
    try {
      await fetch(`${API_URL}/api/locations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Standort gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error('Standort konnte nicht gelöscht werden');
    }
  };

  const handleToggleLocationActive = async (location) => {
    try {
      await fetch(`${API_URL}/api/locations/${location.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ active: !location.active }),
      });
      toast.success('Standort aktualisiert');
      fetchAllData();
    } catch (error) {
      toast.error('Standort konnte nicht aktualisiert werden');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Bitte geben Sie einen Kategorienamen ein');
      return;
    }
    try {
      await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          ...newCategory, 
          slug: newCategory.slug || newCategory.name.toLowerCase().replace(/\s+/g, '-'),
          order: categories.length 
        }),
      });
      toast.success('Kategorie hinzugefügt');
      setNewCategory({ name: '', slug: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Kategorie konnte nicht hinzugefügt werden');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await fetch(`${API_URL}/api/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Kategorie gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error('Kategorie konnte nicht gelöscht werden');
    }
  };

  // Service Category handlers
  const handleAddServiceCategory = async () => {
    if (!newServiceCategory.title) {
      toast.error('Bitte geben Sie einen Kategorietitel ein');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/service-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: newServiceCategory.title,
          description: newServiceCategory.description || null,
          items: [],
          order: serviceCategories.length
        }),
      });

      if (response.ok) {
        toast.success('Service-Kategorie hinzugefügt');
        setNewServiceCategory({ title: '', description: '', items: [] });
        fetchAllData();
      } else {
        toast.error('Service-Kategorie konnte nicht hinzugefügt werden');
      }
    } catch (error) {
      toast.error('Service-Kategorie konnte nicht hinzugefügt werden');
    }
  };

  const handleUpdateServiceCategory = async (categoryId, updatedData) => {
    try {
      const response = await fetch(`${API_URL}/api/service-categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (response.ok) {
        toast.success('Service-Kategorie aktualisiert');
        setEditingServiceCategory(null);
        fetchAllData();
      } else {
        toast.error('Service-Kategorie konnte nicht aktualisiert werden');
      }
    } catch (error) {
      toast.error('Service-Kategorie konnte nicht aktualisiert werden');
    }
  };

  const handleDeleteServiceCategory = async (id) => {
    try {
      await fetch(`${API_URL}/api/service-categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Service-Kategorie gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error('Service-Kategorie konnte nicht gelöscht werden');
    }
  };

  const handleUploadServiceCategoryImage = async (categoryId, file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/service-categories/${categoryId}/image`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        toast.success('Kategorie-Bild hochgeladen');
        fetchAllData();
      } else {
        toast.error('Bild konnte nicht hochgeladen werden');
      }
    } catch (error) {
      toast.error('Bild konnte nicht hochgeladen werden');
    }
  };

  const handleAddServiceItem = (categoryId) => {
    if (!newServiceItem.title || !newServiceItem.description) {
      toast.error('Bitte füllen Sie Titel und Beschreibung aus');
      return;
    }
    
    const category = serviceCategories.find(c => c.id === categoryId);
    if (category) {
      const updatedItems = [...(category.items || []), { ...newServiceItem }];
      handleUpdateServiceCategory(categoryId, { ...category, items: updatedItems });
      setNewServiceItem({ title: '', description: '' });
    }
  };

  const handleRemoveServiceItem = (categoryId, itemIndex) => {
    const category = serviceCategories.find(c => c.id === categoryId);
    if (category) {
      const updatedItems = category.items.filter((_, index) => index !== itemIndex);
      handleUpdateServiceCategory(categoryId, { ...category, items: updatedItems });
    }
  };

  // Client Logo handlers
  const handleUploadLogo = async () => {
    const files = logoInputRef.current?.files;
    if (!files || files.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Datei aus');
      return;
    }
    
    setUploadingLogo(true);
    try {
      let successCount = 0;
      let failCount = 0;

      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Use filename without extension as name

        try {
          const response = await fetch(`${API_URL}/api/client-logos/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} Logo${successCount > 1 ? 's' : ''} hochgeladen`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} Logo${failCount > 1 ? 's' : ''} fehlgeschlagen`);
      }
      
      if (logoInputRef.current) logoInputRef.current.value = '';
      fetchAllData();
    } catch (error) {
      toast.error('Logos konnten nicht hochgeladen werden');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async (id) => {
    try {
      await fetch(`${API_URL}/api/client-logos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Logo gelöscht');
      fetchAllData();
    } catch (error) {
      toast.error('Logo konnte nicht gelöscht werden');
    }
  };

  const handleMoveLogoOrder = async (index, direction) => {
    const newLogos = [...clientLogos];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newLogos.length) return;
    
    // Swap positions
    [newLogos[index], newLogos[newIndex]] = [newLogos[newIndex], newLogos[index]];
    
    // Update order values
    const orderData = newLogos.map((logo, i) => ({ id: logo.id, order: i }));
    
    setClientLogos(newLogos);
    
    try {
      await fetch(`${API_URL}/api/client-logos/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ order: orderData }),
      });
    } catch (error) {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
      fetchAllData();
    }
  };

  // Legal page handlers
  const handleUpdateLegalPage = async (pageType) => {
    try {
      const response = await fetch(`${API_URL}/api/legal/${pageType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(legalPages[pageType]),
      });

      if (response.ok) {
        toast.success('Seite aktualisiert');
      } else {
        toast.error('Seite konnte nicht aktualisiert werden');
      }
    } catch (error) {
      toast.error('Seite konnte nicht aktualisiert werden');
    }
  };

  // Legal page autosave
  const handleLegalPageAutosave = (pageType, field, value) => {
    setLegalPages(prev => ({
      ...prev,
      [pageType]: { ...prev[pageType], [field]: value }
    }));
    triggerAutosave(() => handleUpdateLegalPage(pageType));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Laden</p>
        </div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'content', label: 'Inhalte', icon: FileText },
    { id: 'portals', label: 'Portfolios', icon: Globe },
    { id: 'faq', label: 'FAQ', icon: MessageCircle },
    { id: 'media', label: 'Galerie', icon: Image },
    { id: 'locations', label: 'Standorte', icon: MapPin },
    { id: 'categories', label: 'Kategorien', icon: Layers },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'logos', label: 'Kundenlogos', icon: Users },
    { id: 'legal', label: 'Rechtliches', icon: Scale },
    { id: 'contacts', label: 'Anfragen', icon: Mail, badge: contactSubmissions.filter(c => !c.read).length },
    { id: 'settings', label: 'Einstellungen', icon: Wrench },
  ];

  // Close sidebar when clicking on a tab on mobile
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <Link to="/" className="block">
              <img 
                src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_studio-map/artifacts/iti9qqas_logowl.png"} 
                alt="WHITELABEL COLLECTIVE" 
                className="h-5 w-auto"
              />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                data-testid={`admin-tab-${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  activeTab === item.id 
                    ? 'bg-accent text-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-foreground text-sm font-medium">
                {user?.name?.[0] || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name || user?.email || 'Admin'}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen w-full">
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="font-heading text-lg font-semibold text-foreground capitalize">
                {sidebarItems.find(i => i.id === activeTab)?.label || activeTab}
              </h1>
              <AutosaveStatus status={autosaveStatus} />
            </div>
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground hidden sm:block">
              Website ansehen →
            </Link>
          </div>
        </header>

        <div className="p-4 md:p-6">
          <AnimatePresence mode="wait">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchStats}
                    disabled={loadingStats}
                    data-testid="stats-refresh-btn"
                  >
                    {loadingStats ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TrendingUp className="w-4 h-4 mr-2" />
                    )}
                    Statistiken aktualisieren
                  </Button>
                </div>

                {/* Stats Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Gesamtbesucher</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-foreground" data-testid="stats-total-visitors">
                        {stats?.total_visitors ?? '-'}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Alle Besucher seit Beginn</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Letzte 30 Tage</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-foreground" data-testid="stats-visitors-30d">
                        {stats?.visitors_30d ?? '-'}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Besucher im letzten Monat</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Kontaktanfragen</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-foreground">
                        {contactSubmissions.length}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {contactSubmissions.filter(c => !c.read).length} ungelesen
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Countries & Cities */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <Globe className="w-5 h-5" /> Top Länder
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {!stats ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Klicken Sie auf "Statistiken aktualisieren"</p>
                        </div>
                      ) : stats.top_countries?.length > 0 ? (
                        <div className="space-y-3">
                          {stats.top_countries.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-background rounded-sm">
                              <span className="text-foreground">{item.country}</span>
                              <span className="text-muted-foreground font-medium">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Noch keine Daten vorhanden</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <MapPinned className="w-5 h-5" /> Top Städte
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {!stats ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Klicken Sie auf "Statistiken aktualisieren"</p>
                        </div>
                      ) : stats.top_cities?.length > 0 ? (
                        <div className="space-y-3">
                          {stats.top_cities.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-background rounded-sm">
                              <span className="text-foreground">
                                {item.city} <span className="text-muted-foreground text-sm">({item.country})</span>
                              </span>
                              <span className="text-muted-foreground font-medium">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Noch keine Daten vorhanden</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Visits Chart (Simple) */}
                {stats?.daily_visits?.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" /> Besucher pro Tag (Letzte 30 Tage)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48 flex items-end gap-1">
                        {stats.daily_visits.map((day, index) => {
                          const maxCount = Math.max(...stats.daily_visits.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div
                              key={index}
                              className="flex-1 bg-primary/70 hover:bg-primary transition-colors rounded-t-sm relative group"
                              style={{ height: `${Math.max(height, 2)}%` }}
                              title={`${day.date}: ${day.count} Besucher`}
                            >
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                {day.date}: {day.count}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                        <span>{stats.daily_visits[0]?.date}</span>
                        <span>{stats.daily_visits[stats.daily_visits.length - 1]?.date}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Info Box */}
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">
                      <strong>Hinweis:</strong> Besucher werden basierend auf IP-Adresse und Geolocation erfasst. 
                      Lokale Entwicklungs-IPs werden nicht gezählt.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Content Tab */}
            {activeTab === 'content' && (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Tabs defaultValue="hero" className="w-full">
                  <TabsList className="bg-card border border-border">
                    <TabsTrigger value="hero">Hero</TabsTrigger>
                    <TabsTrigger value="about">Über uns</TabsTrigger>
                    <TabsTrigger value="contact">Kontakt</TabsTrigger>
                    <TabsTrigger value="seo">SEO</TabsTrigger>
                  </TabsList>

                  <TabsContent value="hero" className="space-y-4 mt-6">
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-foreground">Hero Bereich</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-foreground">Überschrift</Label>
                          <Input
                            value={editingContent['hero_hero_headline'] || ''}
                            onChange={(e) => handleContentAutosave('hero_headline', e.target.value, 'hero')}
                            className="bg-background border-border text-foreground"
                            data-testid="hero-headline-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Untertext</Label>
                          <Textarea
                            value={editingContent['hero_hero_subtext'] || ''}
                            onChange={(e) => handleContentAutosave('hero_subtext', e.target.value, 'hero')}
                            className="bg-background border-border text-foreground"
                            data-testid="hero-subtext-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">CTA Button</Label>
                          <Input
                            value={editingContent['hero_hero_cta'] || ''}
                            onChange={(e) => handleContentAutosave('hero_cta', e.target.value, 'hero')}
                            className="bg-background border-border text-foreground"
                            data-testid="hero-cta-input"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="about" className="space-y-4 mt-6">
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-foreground">Über uns Bereich</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-foreground">Überschrift</Label>
                          <Input
                            value={editingContent['about_about_headline'] || ''}
                            onChange={(e) => handleContentAutosave('about_headline', e.target.value, 'about')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Text</Label>
                          <Textarea
                            value={editingContent['about_about_text'] || ''}
                            onChange={(e) => handleContentAutosave('about_text', e.target.value, 'about')}
                            className="bg-background border-border text-foreground min-h-[150px]"
                          />
                        </div>
                        
                        {/* Stats / Zahlen */}
                        <div className="pt-4 border-t border-border">
                          <Label className="text-foreground font-semibold mb-4 block">Statistiken / Zahlen</Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">Statistik 1</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={editingContent['about_stat_1_number'] || '13+'}
                                  onChange={(e) => handleContentAutosave('stat_1_number', e.target.value, 'about')}
                                  placeholder="13+"
                                  className="bg-background border-border text-foreground w-24"
                                />
                                <Input
                                  value={editingContent['about_stat_1_label'] || 'Years Experience'}
                                  onChange={(e) => handleContentAutosave('stat_1_label', e.target.value, 'about')}
                                  placeholder="Years Experience"
                                  className="bg-background border-border text-foreground flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">Statistik 2</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={editingContent['about_stat_2_number'] || '200+'}
                                  onChange={(e) => handleContentAutosave('stat_2_number', e.target.value, 'about')}
                                  placeholder="200+"
                                  className="bg-background border-border text-foreground w-24"
                                />
                                <Input
                                  value={editingContent['about_stat_2_label'] || 'Projects Delivered'}
                                  onChange={(e) => handleContentAutosave('stat_2_label', e.target.value, 'about')}
                                  placeholder="Projects Delivered"
                                  className="bg-background border-border text-foreground flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">Statistik 3</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={editingContent['about_stat_3_number'] || '50+'}
                                  onChange={(e) => handleContentAutosave('stat_3_number', e.target.value, 'about')}
                                  placeholder="50+"
                                  className="bg-background border-border text-foreground w-24"
                                />
                                <Input
                                  value={editingContent['about_stat_3_label'] || 'Agency Partners'}
                                  onChange={(e) => handleContentAutosave('stat_3_label', e.target.value, 'about')}
                                  placeholder="Agency Partners"
                                  className="bg-background border-border text-foreground flex-1"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">Statistik 4</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={editingContent['about_stat_4_number'] || '8'}
                                  onChange={(e) => handleContentAutosave('stat_4_number', e.target.value, 'about')}
                                  placeholder="8"
                                  className="bg-background border-border text-foreground w-24"
                                />
                                <Input
                                  value={editingContent['about_stat_4_label'] || 'Global Locations'}
                                  onChange={(e) => handleContentAutosave('stat_4_label', e.target.value, 'about')}
                                  placeholder="Global Locations"
                                  className="bg-background border-border text-foreground flex-1"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="contact" className="space-y-4 mt-6">
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-foreground">Kontakt Bereich</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-foreground">Überschrift</Label>
                          <Input
                            value={editingContent['contact_contact_headline'] || 'Ready to Create?'}
                            onChange={(e) => handleContentAutosave('contact_headline', e.target.value, 'contact')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Beschreibung</Label>
                          <Textarea
                            value={editingContent['contact_contact_description'] || ''}
                            onChange={(e) => handleContentAutosave('contact_description', e.target.value, 'contact')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Anzeige-Email</Label>
                          <Input
                            value={editingContent['contact_contact_email'] || 'office@whitelabel-collective.com'}
                            onChange={(e) => handleContentAutosave('contact_email', e.target.value, 'contact')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Telefon</Label>
                          <Input
                            value={editingContent['contact_contact_phone'] || '+49 30 123 456 789'}
                            onChange={(e) => handleContentAutosave('contact_phone', e.target.value, 'contact')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Standort</Label>
                          <Input
                            value={editingContent['contact_contact_location'] || 'Berlin, Germany'}
                            onChange={(e) => handleContentAutosave('contact_location', e.target.value, 'contact')}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="seo" className="space-y-4 mt-6">
                    <Card className="bg-card border-border">
                      <CardHeader>
                        <CardTitle className="text-foreground">SEO Settings (Social Sharing & Google)</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Diese Einstellungen bestimmen, wie Ihre Website in Google-Suchergebnissen und beim Teilen auf WhatsApp, Facebook, Twitter etc. erscheint.
                        </p>
                        <div>
                          <Label className="text-foreground">Site Title</Label>
                          <Input
                            value={settings.seo_site_title || ''}
                            onChange={(e) => setSettings({ ...settings, seo_site_title: e.target.value })}
                            placeholder="WHITE LABEL collective"
                            className="bg-background border-border text-foreground"
                            data-testid="seo-title-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Site Description</Label>
                          <Textarea
                            value={settings.seo_site_description || ''}
                            onChange={(e) => setSettings({ ...settings, seo_site_description: e.target.value })}
                            placeholder="Professional video production and photography for brands and agencies."
                            className="bg-background border-border text-foreground"
                            data-testid="seo-description-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Site URL</Label>
                          <Input
                            value={settings.seo_site_url || ''}
                            onChange={(e) => setSettings({ ...settings, seo_site_url: e.target.value })}
                            placeholder="https://whitelabel-collective.com"
                            className="bg-background border-border text-foreground"
                            data-testid="seo-url-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Vorschaubild (OG Image)</Label>
                          <div className="flex items-start gap-4 mt-2">
                            {settings.seo_og_image && (
                              <div className="w-40 h-24 bg-card border border-border rounded-sm overflow-hidden flex-shrink-0">
                                <img 
                                  src={settings.seo_og_image.startsWith('/api') ? `${API_URL}${settings.seo_og_image}` : settings.seo_og_image}
                                  alt="OG Preview"
                                  className="w-full h-full object-cover"
                                  data-testid="seo-image-preview"
                                />
                              </div>
                            )}
                            <div className="flex-1">
                              <Input
                                ref={seoImageInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleUploadSeoImage(e.target.files[0]);
                                  }
                                }}
                                className="bg-background border-border text-foreground"
                                disabled={uploadingSeoImage}
                                data-testid="seo-image-upload"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Empfohlen: 1200x630px (JPG, PNG oder WebP)
                              </p>
                              {uploadingSeoImage && (
                                <p className="text-xs text-primary mt-1 flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" /> Wird hochgeladen...
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => {
                            handleUpdateSetting('seo_site_title', settings.seo_site_title || '');
                            handleUpdateSetting('seo_site_description', settings.seo_site_description || '');
                            handleUpdateSetting('seo_site_url', settings.seo_site_url || '');
                          }}
                          data-testid="seo-save-btn"
                        >
                          <Save className="w-4 h-4 mr-2" /> SEO Settings speichern
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </motion.div>
            )}

            {/* Portfolios Tab */}
            {activeTab === 'portals' && (
              <motion.div
                key="portals"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <PortalsAdmin
                  token={token}
                  portals={portals}
                  media={media}
                  onRefresh={fetchAllData}
                />
              </motion.div>
            )}

            {/* FAQ Tab */}
            {activeTab === 'faq' && (
              <motion.div
                key="faq"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <FaqAdmin
                  token={token}
                  items={faqItems}
                  onRefresh={fetchAllData}
                />
              </motion.div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <motion.div
                key="media"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Bulk Upload Images */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <ImagePlus className="w-5 h-5" /> Bilder hochladen (Bulk)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Wählen Sie mehrere Bilder aus. Alle Bilder werden mit der gleichen Kategorie gespeichert.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <Label className="text-foreground">Bilddateien</Label>
                          <Input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => setUploadFile(e.target.files)}
                            className="bg-background border-border text-foreground"
                            data-testid="bulk-image-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Mehrere Bilder auswählen (Strg/Cmd+Klick)
                          </p>
                        </div>
                        <div>
                          <Label className="text-foreground">Kategorie</Label>
                          <Input
                            value={newMedia.categories}
                            onChange={(e) => setNewMedia({ ...newMedia, categories: e.target.value })}
                            placeholder="fashion, nightlife"
                            className="bg-background border-border text-foreground"
                            data-testid="bulk-image-category"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={async () => {
                          if (!uploadFile || uploadFile.length === 0) {
                            toast.error('Bitte wählen Sie mindestens eine Datei aus');
                            return;
                          }
                          setUploading(true);
                          try {
                            const formData = new FormData();
                            for (let i = 0; i < uploadFile.length; i++) {
                              formData.append('files', uploadFile[i]);
                            }
                            formData.append('category', newMedia.categories || '');
                            
                            const response = await fetch(`${API_URL}/api/media/bulk-upload`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData,
                            });
                            
                            if (response.ok) {
                              const result = await response.json();
                              toast.success(`${result.success} Bild(er) hochgeladen`);
                              if (result.failed > 0) {
                                toast.error(`${result.failed} Bild(er) fehlgeschlagen`);
                              }
                              setUploadFile(null);
                              setNewMedia({ ...newMedia, categories: '' });
                              if (fileInputRef.current) fileInputRef.current.value = '';
                              fetchAllData();
                            } else {
                              toast.error('Upload fehlgeschlagen');
                            }
                          } catch (error) {
                            toast.error('Fehler beim Hochladen');
                          } finally {
                            setUploading(false);
                          }
                        }} 
                        disabled={uploading} 
                        className="w-full md:w-auto" 
                        data-testid="bulk-image-upload-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lädt...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" /> Bilder hochladen
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Add Video with Poster */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Video className="w-5 h-5" /> Video hinzufügen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Fügen Sie ein Video per URL hinzu und laden Sie optional ein eigenes Thumbnail/Poster-Bild hoch.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-foreground">Video URL</Label>
                          <Input
                            value={newMedia.url}
                            onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })}
                            placeholder="https://..."
                            className="bg-background border-border text-foreground"
                            data-testid="video-url-input"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Kategorie</Label>
                          <Input
                            value={newMedia.categories}
                            onChange={(e) => setNewMedia({ ...newMedia, categories: e.target.value })}
                            placeholder="fashion, nightlife"
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-foreground">Poster/Thumbnail (optional)</Label>
                        <Input
                          id="video-poster-input"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="bg-background border-border text-foreground"
                          data-testid="video-poster-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Ein weißer Play-Button wird automatisch auf dem Thumbnail angezeigt.
                        </p>
                      </div>
                      <Button 
                        onClick={async () => {
                          if (!newMedia.url) {
                            toast.error('Bitte geben Sie eine Video-URL ein');
                            return;
                          }
                          setUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append('video_url', newMedia.url);
                            formData.append('category', newMedia.categories || '');
                            
                            const posterInput = document.getElementById('video-poster-input');
                            if (posterInput?.files?.[0]) {
                              formData.append('poster_file', posterInput.files[0]);
                            }
                            
                            const response = await fetch(`${API_URL}/api/media/video-upload`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData,
                            });
                            
                            if (response.ok) {
                              toast.success('Video hinzugefügt');
                              setNewMedia({ title: '', type: 'image', url: '', categories: '', featured: false });
                              if (posterInput) posterInput.value = '';
                              fetchAllData();
                            } else {
                              toast.error('Video konnte nicht hinzugefügt werden');
                            }
                          } catch (error) {
                            toast.error('Fehler beim Hinzufügen');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        className="w-full md:w-auto"
                        data-testid="video-add-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lädt...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" /> Video hinzufügen
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Media Grid */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Image className="w-5 h-5" /> Galerie ({media.length} Einträge)
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {media.map((item) => (
                        <div key={item.id} className="relative overflow-hidden group rounded-sm border border-border bg-background">
                          <div className="aspect-square relative">
                            {item.type === 'video' ? (
                              <>
                                <img
                                  src={getVideoThumbnail(item)}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback for YouTube if maxres doesn't exist
                                    if (e.target.src.includes('maxresdefault')) {
                                      e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
                                    } else if (!e.target.src.includes('placehold.co')) {
                                      e.target.src = 'https://placehold.co/400x400/1a1a1a/666666?text=Video';
                                    }
                                  }}
                                />
                                {/* Play button overlay */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                    <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <img
                                src={item.url.startsWith('/api') ? `${API_URL}${item.url}` : item.url}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            )}
                            <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button size="icon" variant="ghost" onClick={() => handleToggleFeatured(item)} className={item.featured ? 'text-yellow-500' : 'text-muted-foreground'}>
                                <Star className="w-5 h-5" fill={item.featured ? 'currentColor' : 'none'} />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDeleteMedia(item.id)} className="text-destructive">
                                <Trash2 className="w-5 h-5" />
                              </Button>
                            </div>
                            {item.type === 'video' && (
                              <div className="absolute top-2 left-2 px-2 py-1 bg-background/80 text-xs text-foreground rounded-sm">VIDEO</div>
                            )}
                          </div>
                          <div className="p-2">
                            <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.categories?.join(', ') || '-'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {media.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Noch keine Medien vorhanden</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Locations Tab */}
            {activeTab === 'locations' && (
              <motion.div
                key="locations"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Standort hinzufügen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-foreground">Name</Label>
                        <Input
                          value={newLocation.name}
                          onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                          placeholder="Stadtname"
                          className="bg-background border-border text-foreground"
                          data-testid="location-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Längengrad</Label>
                        <Input
                          type="number"
                          step="any"
                          value={newLocation.coordinates[0]}
                          onChange={(e) => setNewLocation({ ...newLocation, coordinates: [parseFloat(e.target.value) || 0, newLocation.coordinates[1]] })}
                          className="bg-background border-border text-foreground"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Breitengrad</Label>
                        <Input
                          type="number"
                          step="any"
                          value={newLocation.coordinates[1]}
                          onChange={(e) => setNewLocation({ ...newLocation, coordinates: [newLocation.coordinates[0], parseFloat(e.target.value) || 0] })}
                          className="bg-background border-border text-foreground"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Beschreibung</Label>
                        <Input
                          value={newLocation.description}
                          onChange={(e) => setNewLocation({ ...newLocation, description: e.target.value })}
                          placeholder="Optional"
                          className="bg-background border-border text-foreground"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handleAddLocation} className="w-full" data-testid="location-add-btn">
                          <Plus className="w-4 h-4 mr-2" /> Hinzufügen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {locations.map((location) => (
                    <Card key={location.id} className={`bg-card border-border ${!location.active ? 'opacity-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-foreground">{location.name}</h3>
                          <div className="flex items-center gap-2">
                            <Switch checked={location.active} onCheckedChange={() => handleToggleLocationActive(location)} />
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteLocation(location.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{location.coordinates[0]}, {location.coordinates[1]}</p>
                        {location.description && <p className="text-sm text-muted-foreground mt-2">{location.description}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <motion.div
                key="categories"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Kategorie hinzufügen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Label className="text-foreground">Name</Label>
                        <Input
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                          placeholder="Kategoriename"
                          className="bg-background border-border text-foreground"
                          data-testid="category-name-input"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-foreground">Slug</Label>
                        <Input
                          value={newCategory.slug}
                          onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                          placeholder="kategorie-slug"
                          className="bg-background border-border text-foreground"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handleAddCategory} data-testid="category-add-btn">
                          <Plus className="w-4 h-4 mr-2" /> Hinzufügen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  {categories.map((category) => (
                    <Card key={category.id} className="bg-card border-border">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                          <div>
                            <p className="font-medium text-foreground">{category.name}</p>
                            <p className="text-xs text-muted-foreground">/{category.slug}</p>
                          </div>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteCategory(category.id)} className="text-destructive" disabled={category.slug === 'all'}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <motion.div
                key="services"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Add new service category */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Neue Service-Kategorie hinzufügen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_auto] gap-4">
                      <div>
                        <Label className="text-foreground">Kategorie-Titel</Label>
                        <Input
                          value={newServiceCategory.title}
                          onChange={(e) => setNewServiceCategory({ ...newServiceCategory, title: e.target.value })}
                          placeholder="z.B. Creative Production, Websites..."
                          className="bg-background border-border text-foreground"
                          data-testid="service-category-title-input"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Kurzbeschreibung (steht oben auf der Karte)</Label>
                        <Input
                          value={newServiceCategory.description}
                          onChange={(e) => setNewServiceCategory({ ...newServiceCategory, description: e.target.value })}
                          placeholder="Ein Satz, worum es in dieser Leistung geht"
                          className="bg-background border-border text-foreground"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handleAddServiceCategory} data-testid="service-category-add-btn">
                          <Plus className="w-4 h-4 mr-2" /> Hinzufügen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Existing service categories */}
                {serviceCategories.length === 0 ? (
                  <Card className="bg-card border-border">
                    <CardContent className="py-12 text-center">
                      <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">Noch keine Service-Kategorien vorhanden.</p>
                      <p className="text-sm text-muted-foreground">Fügen Sie oben Ihre erste Kategorie hinzu.</p>
                    </CardContent>
                  </Card>
                ) : (
                  serviceCategories.map((category) => (
                    <Card key={category.id} className="bg-card border-border">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-foreground flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                            {category.title}
                            <span className="text-sm font-normal text-muted-foreground">
                              ({category.items?.length || 0} Services)
                            </span>
                          </CardTitle>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleDeleteServiceCategory(category.id)} 
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Category Image Upload */}
                        <div className="p-4 bg-background rounded-sm border border-border">
                          <div className="flex items-start gap-4">
                            <div className="w-32 h-20 bg-card border border-border rounded-sm overflow-hidden flex items-center justify-center">
                              {category.image_url ? (
                                <img 
                                  src={category.image_url.startsWith('/api') ? `${API_URL}${category.image_url}` : category.image_url}
                                  alt={category.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <ImagePlus className="w-8 h-8 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1">
                              <Label className="text-foreground text-sm">Kategorie-Bild (wird links auf der Services-Seite angezeigt)</Label>
                              <div className="flex gap-2 mt-2">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleUploadServiceCategoryImage(category.id, e.target.files[0]);
                                    }
                                  }}
                                  className="bg-background border-border text-foreground"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Existing items */}
                        {category.items && category.items.length > 0 && (
                          <div className="space-y-2">
                            {category.items.map((item, index) => (
                              <div key={index} className="p-3 bg-background rounded-sm border border-border flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <p className="font-medium text-foreground">{item.title}</p>
                                  <p className="text-sm text-muted-foreground">{item.description}</p>
                                </div>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleRemoveServiceItem(category.id, index)}
                                  className="text-destructive flex-shrink-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add new item form */}
                        <div className="pt-4 border-t border-border">
                          <p className="text-sm font-medium text-foreground mb-3">Neuen Service hinzufügen:</p>
                          <ServiceItemForm 
                            categoryId={category.id}
                            onAdd={(title, description) => {
                              const updatedItems = [...(category.items || []), { title, description }];
                              handleUpdateServiceCategory(category.id, { ...category, items: updatedItems });
                            }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </motion.div>
            )}

            {/* Client Logos Tab */}
            {activeTab === 'logos' && (
              <motion.div
                key="logos"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Upload className="w-5 h-5" /> Kundenlogos hochladen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Laden Sie Logos Ihrer Kunden hoch. Sie können mehrere Dateien auf einmal auswählen.
                      Empfohlen: PNG oder SVG mit transparentem Hintergrund.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <Label className="text-foreground">Logodateien auswählen</Label>
                        <Input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="bg-background border-border text-foreground"
                          data-testid="logo-file-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Mehrfachauswahl: Strg/Cmd gedrückt halten beim Auswählen
                        </p>
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handleUploadLogo} disabled={uploadingLogo} className="w-full sm:w-auto" data-testid="logo-upload-btn">
                          {uploadingLogo ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lädt...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Hochladen</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Logos Grid */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Users className="w-5 h-5" /> Logos sortieren ({clientLogos.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Verwenden Sie die Pfeile, um die Reihenfolge der Logos zu ändern.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {clientLogos.map((logo, index) => (
                        <div key={logo.id} className="bg-background border border-border rounded-sm overflow-hidden group">
                          <div className="aspect-video relative flex items-center justify-center p-4">
                            <img
                              src={logo.url.startsWith('/api') ? `${API_URL}${logo.url}` : logo.url}
                              alt={logo.name}
                              className="max-h-14 w-auto object-contain filter brightness-0 invert"
                            />
                          </div>
                          <div className="flex items-center justify-between p-2 border-t border-border bg-card">
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleMoveLogoOrder(index, 'up')}
                                disabled={index === 0}
                                className="h-8 w-8"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleMoveLogoOrder(index, 'down')}
                                disabled={index === clientLogos.length - 1}
                                className="h-8 w-8"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>
                            <span className="text-xs text-muted-foreground">#{index + 1}</span>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              onClick={() => handleDeleteLogo(logo.id)} 
                              className="text-destructive h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {clientLogos.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Kundenlogos hochgeladen.</p>
                    <p className="text-sm">Laden Sie Logos hoch, um sie im Karussell anzuzeigen.</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Legal Pages Tab */}
            {activeTab === 'legal' && (
              <motion.div
                key="legal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Scale className="w-5 h-5" /> Rechtliche Seiten
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeLegalPage} onValueChange={setActiveLegalPage}>
                      <TabsList className="bg-background border border-border mb-6">
                        <TabsTrigger value="privacy">Datenschutz</TabsTrigger>
                        <TabsTrigger value="terms">AGB</TabsTrigger>
                        <TabsTrigger value="imprint">Impressum</TabsTrigger>
                      </TabsList>

                      {['privacy', 'terms', 'imprint'].map((pageType) => (
                        <TabsContent key={pageType} value={pageType} className="space-y-4">
                          <div>
                            <Label className="text-foreground">Seitentitel</Label>
                            <Input
                              value={legalPages[pageType]?.title || ''}
                              onChange={(e) => handleLegalPageAutosave(pageType, 'title', e.target.value)}
                              className="bg-background border-border text-foreground"
                              data-testid={`legal-${pageType}-title`}
                            />
                          </div>
                          <div>
                            <Label className="text-foreground">Inhalt (HTML erlaubt)</Label>
                            <Textarea
                              value={legalPages[pageType]?.body || ''}
                              onChange={(e) => handleLegalPageAutosave(pageType, 'body', e.target.value)}
                              className="bg-background border-border text-foreground min-h-[400px] font-mono text-sm"
                              placeholder="<h2>Überschrift</h2><p>Ihr Text hier...</p>"
                              data-testid={`legal-${pageType}-body`}
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Sie können HTML-Tags verwenden: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href="..."&gt;
                            </p>
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Contact Submissions Tab */}
            {activeTab === 'contacts' && (
              <motion.div
                key="contacts"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Mail className="w-5 h-5" /> Kontaktanfragen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-sm mb-6">
                      <p className="text-amber-500 font-medium">⚠️ Hinweis: E-Mail-Versand nicht konfiguriert</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Kontaktanfragen werden derzeit nur in der Datenbank gespeichert. 
                        Um E-Mails zu versenden, wird eine E-Mail-Integration (z.B. SendGrid, Resend) benötigt.
                      </p>
                    </div>

                    {contactSubmissions.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Noch keine Kontaktanfragen eingegangen.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {contactSubmissions.map((submission) => (
                          <div key={submission.id} className={`p-4 bg-background rounded-sm border ${submission.read ? 'border-border' : 'border-primary'}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="font-medium text-foreground">{submission.name}</p>
                                  {!submission.read && (
                                    <span className="px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded">Neu</span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{submission.email}</p>
                                {submission.company && (
                                  <p className="text-sm text-muted-foreground">Firma: {submission.company}</p>
                                )}
                                {submission.budget && (
                                  <p className="text-sm text-muted-foreground">Budget: {submission.budget}</p>
                                )}
                                <p className="text-sm text-foreground mt-3">{submission.message}</p>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {new Date(submission.created_at).toLocaleDateString('de-DE', { 
                                    day: '2-digit', month: '2-digit', year: 'numeric', 
                                    hour: '2-digit', minute: '2-digit' 
                                  })}
                                </p>
                              </div>
                              <a 
                                href={`mailto:${submission.email}`}
                                className="p-2 text-muted-foreground hover:text-foreground"
                              >
                                <Mail className="w-5 h-5" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Hero Bereich</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-background rounded-sm border border-border">
                      <div>
                        <h3 className="font-medium text-foreground">Hero Text & Buttons anzeigen</h3>
                        <p className="text-sm text-muted-foreground mt-1">Wenn deaktiviert, wird nur das Hintergrundvideo angezeigt.</p>
                      </div>
                      <Switch 
                        checked={settings.show_hero_text} 
                        onCheckedChange={(v) => handleUpdateSetting('show_hero_text', v)} 
                        data-testid="settings-hero-text-toggle"
                      />
                    </div>
                    <div>
                      <Label className="text-foreground">Hero Video (YouTube URL)</Label>
                      <Input
                        value={settings.hero_video_url || ''}
                        onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
                        placeholder="https://youtu.be/Y8Bb3d8O7_g"
                        className="bg-background border-border text-foreground"
                        data-testid="settings-hero-video-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        YouTube URL für das Hintergrundvideo. Unterstützt: youtu.be/..., youtube.com/watch?v=...
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2" 
                        onClick={() => handleUpdateSetting('hero_video_url', settings.hero_video_url)}
                        data-testid="settings-hero-video-save"
                      >
                        <Save className="w-4 h-4 mr-2" /> Video speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" /> WhatsApp Business
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground">WhatsApp Link</Label>
                        <Input
                          value={settings.whatsapp_link || ''}
                          onChange={(e) => setSettings({ ...settings, whatsapp_link: e.target.value })}
                          placeholder="https://wa.me/49123456789"
                          className="bg-background border-border text-foreground"
                          data-testid="settings-whatsapp-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Format: https://wa.me/LÄNDERVORWAHL+NUMMER (ohne Leerzeichen, z.B. https://wa.me/49123456789)
                        </p>
                        <Button 
                          size="sm" 
                          className="mt-2" 
                          onClick={() => handleUpdateSetting('whatsapp_link', settings.whatsapp_link)}
                          data-testid="settings-whatsapp-save"
                        >
                          <Save className="w-4 h-4 mr-2" /> Speichern
                        </Button>
                      </div>
                      {settings.whatsapp_link && (
                        <div className="p-4 bg-[#25D366]/10 border border-[#25D366]/20 rounded-sm">
                          <p className="text-[#25D366] font-medium flex items-center gap-2">
                            <MessageCircle className="w-4 h-4" /> WhatsApp Button ist aktiv
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Der WhatsApp-Button wird im Kontaktbereich angezeigt.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Kontaktformular Empfänger</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground">E-Mail-Adresse</Label>
                        <Input
                          value={settings.contact_email || 'office@whitelabel-collective.com'}
                          onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                          placeholder="office@whitelabel-collective.com"
                          className="bg-background border-border text-foreground"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Kontaktformular-Einträge werden in der Datenbank gespeichert.</p>
                        <Button size="sm" className="mt-2" onClick={() => handleUpdateSetting('contact_email', settings.contact_email)}>
                          <Save className="w-4 h-4 mr-2" /> Speichern
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <DollarSign className="w-5 h-5" /> Budget-Währung
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground">Währung im Kontaktformular</Label>
                        <Select 
                          value={settings.budget_currency || 'EUR'} 
                          onValueChange={(v) => handleUpdateSetting('budget_currency', v)}
                        >
                          <SelectTrigger className="bg-background border-border text-foreground w-full max-w-xs">
                            <SelectValue placeholder="Währung wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">€ Euro (EUR)</SelectItem>
                            <SelectItem value="USD">$ US-Dollar (USD)</SelectItem>
                            <SelectItem value="AED">د.إ Dirham (AED)</SelectItem>
                            <SelectItem value="GBP">£ Pfund (GBP)</SelectItem>
                            <SelectItem value="CHF">Fr. Franken (CHF)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Diese Währung wird im Budget-Dropdown des Kontaktformulars angezeigt.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Wartungsmodus</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-background rounded-sm border border-border">
                        <div>
                          <h3 className="font-medium text-foreground">Wartungsmodus aktivieren</h3>
                          <p className="text-sm text-muted-foreground mt-1">Besucher sehen eine "Upgrade in Arbeit" Nachricht.</p>
                        </div>
                        <Switch 
                          checked={settings.maintenance_mode} 
                          onCheckedChange={(v) => handleUpdateSetting('maintenance_mode', v)}
                          data-testid="settings-maintenance-toggle"
                        />
                      </div>
                      
                      {settings.maintenance_mode && (
                        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
                          <p className="text-destructive font-medium">⚠️ Wartungsmodus ist AKTIV</p>
                          <p className="text-sm text-destructive/80 mt-1">Ihre Website ist für Besucher nicht sichtbar.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">E-Mail-Versand (Resend) - Master-Passwort erforderlich</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground">Resend API Key</Label>
                        <Input
                          type="password"
                          id="resend_api_key_input"
                          value={settings.resend_api_key || ''}
                          onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                          placeholder="re_..."
                          className="bg-background border-border text-foreground"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          API Key von <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a>. 
                          Domain: mail.whitelabel-collective.com
                        </p>
                      </div>
                      <div>
                        <Label className="text-foreground">Master-Passwort</Label>
                        <Input
                          type="password"
                          value={masterPasswordInput}
                          onChange={(e) => setMasterPasswordInput(e.target.value)}
                          placeholder="Master-Passwort eingeben"
                          className="bg-background border-border text-foreground"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Zum Speichern des API Keys wird das Master-Passwort benötigt.
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={async () => {
                          const apiKey = settings.resend_api_key;
                          if (!masterPasswordInput) {
                            toast.error('Bitte Master-Passwort eingeben');
                            return;
                          }
                          try {
                            const response = await fetch(`${API_URL}/api/settings/resend`, {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                              },
                              body: JSON.stringify({ resend_api_key: apiKey || '', master_password: masterPasswordInput }),
                            });
                            if (response.ok) {
                              toast.success('API Key gespeichert');
                              setMasterPasswordInput('');
                            } else {
                              toast.error('Ungültiges Master-Passwort');
                            }
                          } catch (error) {
                            toast.error('Fehler beim Speichern');
                          }
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" /> API Key speichern
                      </Button>
                      {settings.resend_api_key && (
                        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm">
                          <p className="text-green-500 font-medium">✓ E-Mail-Versand konfiguriert</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Kontaktanfragen werden per E-Mail gesendet.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Empfänger E-Mail</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground">E-Mail-Adresse für Kontaktanfragen</Label>
                        <Input
                          type="email"
                          value={settings.contact_email || ''}
                          onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                          placeholder="office@whitelabel-collective.com"
                          className="bg-background border-border text-foreground"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          An diese Adresse werden Kontaktanfragen gesendet.
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleUpdateSetting('contact_email', settings.contact_email)}
                      >
                        <Save className="w-4 h-4 mr-2" /> E-Mail speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Social Media Links</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Diese Links werden im Footer der Website angezeigt. Leer lassen um den Link auszublenden.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-foreground">Instagram</Label>
                          <Input
                            value={settings.social_instagram || ''}
                            onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
                            placeholder="https://instagram.com/..."
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">LinkedIn</Label>
                          <Input
                            value={settings.social_linkedin || ''}
                            onChange={(e) => setSettings({ ...settings, social_linkedin: e.target.value })}
                            placeholder="https://linkedin.com/company/..."
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">YouTube</Label>
                          <Input
                            value={settings.social_youtube || ''}
                            onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
                            placeholder="https://youtube.com/@..."
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">TikTok</Label>
                          <Input
                            value={settings.social_tiktok || ''}
                            onChange={(e) => setSettings({ ...settings, social_tiktok: e.target.value })}
                            placeholder="https://tiktok.com/@..."
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Facebook</Label>
                          <Input
                            value={settings.social_facebook || ''}
                            onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
                            placeholder="https://facebook.com/..."
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          handleUpdateSetting('social_instagram', settings.social_instagram || '');
                          handleUpdateSetting('social_linkedin', settings.social_linkedin || '');
                          handleUpdateSetting('social_youtube', settings.social_youtube || '');
                          handleUpdateSetting('social_tiktok', settings.social_tiktok || '');
                          handleUpdateSetting('social_facebook', settings.social_facebook || '');
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" /> Social Links speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Shield className="w-5 h-5" /> Spam-Schutz (Cloudflare Turnstile)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Cloudflare Turnstile schützt Ihr Kontaktformular vor Spam-Bots. 
                        Holen Sie sich Ihre API-Keys unter{' '}
                        <a 
                          href="https://dash.cloudflare.com/?to=/:account/turnstile" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="underline text-primary"
                        >
                          dash.cloudflare.com/turnstile
                        </a>
                      </p>
                      <div>
                        <Label className="text-foreground">Site Key</Label>
                        <Input
                          value={settings.turnstile_site_key || ''}
                          onChange={(e) => setSettings({ ...settings, turnstile_site_key: e.target.value })}
                          placeholder="0x..."
                          className="bg-background border-border text-foreground"
                          data-testid="turnstile-site-key-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Öffentlicher Key für das Widget im Frontend
                        </p>
                      </div>
                      <div>
                        <Label className="text-foreground">Secret Key</Label>
                        <Input
                          type="password"
                          value={settings.turnstile_secret_key || ''}
                          onChange={(e) => setSettings({ ...settings, turnstile_secret_key: e.target.value })}
                          placeholder="0x..."
                          className="bg-background border-border text-foreground"
                          data-testid="turnstile-secret-key-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Geheimer Key für die Server-Validierung
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          handleUpdateSetting('turnstile_site_key', settings.turnstile_site_key || '');
                          handleUpdateSetting('turnstile_secret_key', settings.turnstile_secret_key || '');
                        }}
                        data-testid="turnstile-save-btn"
                      >
                        <Save className="w-4 h-4 mr-2" /> Turnstile speichern
                      </Button>
                      {settings.turnstile_site_key && settings.turnstile_secret_key && (
                        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm">
                          <p className="text-green-500 font-medium flex items-center gap-2">
                            <Shield className="w-4 h-4" /> Spam-Schutz konfiguriert
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Das Kontaktformular ist jetzt durch Cloudflare Turnstile geschützt.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
